// 設定 › 組織結構 的結構樹（SPEC #org-tree-ui 第 2 點；數字 = 結構總數）：GET /v1/orgs/{org}/nodes 按節點延遲載入。
//   - node_admin 只拿得到自己的有效子樹（被中斷繼承擋住的不列、不計數、不放佔位列——伺服器就不回）
//   - 🔒 中斷繼承只給 owner / org_admin（伺服器只回給他們；這裡再擋一次 showLock）
//   - 家戶可以多選（≤ 500）→〔移到…〕〔指定管理人…〕；戶列對 owner / org_admin / org.sites 標「未指派」
//   - pick 模式（移動的目標）：只列節點、不列戶；被搬的節點自己與子孫不能選
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { structCount } from '../../lib/orgTreeUi';
import { useQuery } from '@tanstack/react-query';
import type { MyOrg } from '../../lib/planeApi';
import { treeApi, type OrgNode, type OrgTreeItem, type TreeSettings } from '../../lib/orgTreeApi';
import { leafText, levelText } from '../../lib/levels';
import { toggle } from './DelegatedNodePicker';

export interface SelNode {
  node_id: string;
  name: string;
  level_label: string | null;
  isRoot: boolean;
}

export const BATCH_MAX = 500;

export function useRootLevel(org: MyOrg) {
  return useQuery({ queryKey: ['ot', org.org_id, 'nodes', ''], queryFn: () => treeApi.children(org), retry: false });
}

/** 根節點 id：平台回 root 就用它；不然用頂層項目的 parent_node_id。 */
export function rootIdOf(d: { items: OrgTreeItem[]; root?: OrgNode } | undefined): string {
  return d?.root?.node_id ?? d?.items.find((x) => x.parent_node_id)?.parent_node_id ?? '';
}

export function StructureTree(props: {
  org: MyOrg;
  settings?: TreeSettings | null;
  selected?: string;
  onSelect?(n: SelNode): void;
  checked?: Map<string, OrgTreeItem>;
  onCheck?(it: OrgTreeItem, on: boolean): void;
  showLock: boolean;
  showUnassigned: boolean;
  /** 移動目標挑選：只列節點，exclude 裡的（被搬的節點）與它底下不列 */
  pick?: { exclude?: string };
  /** 多選節點（委派精靈）：disabled = 勾了「整個組織」 */
  nodeChecks?: { set: Set<string>; toggle(it: OrgTreeItem): void; disabled?: boolean };
  /** 檢視 / 群組編輯的來源：每列〔＋〕 */
  onAdd?(it: OrgTreeItem): void;
  /** 〔給存取權〕精靈的「哪裡」：戶也可以點選（單選，跟節點的 selected 互斥） */
  onPickHome?(it: OrgTreeItem): void;
  selectedHome?: string;
}) {
  const { t } = useTranslation();
  const { org } = props;
  const root = useRootLevel(org);
  const rootId = rootIdOf(root.data);
  const [q, setQ] = useState('');
  const [sq, setSq] = useState('');
  const search = useQuery({
    queryKey: ['ot', org.org_id, 'nodes-q', sq],
    queryFn: () => treeApi.nodes(org, { q: sq, limit: 50 }),
    enabled: sq.length >= 2,
  });
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  if (root.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (root.isError) return <p className="muted">{t('ot.no_structure_access')}</p>;
  const rootName = t('ot.whole_org_name', { org: org.display_name });
  return (
    <div className="ot-struct" data-testid={props.pick ? 'struct-pick' : 'struct-tree'}>
      {!props.pick && (
        <form
          className="ot-search"
          onSubmit={(e) => {
            e.preventDefault();
            setSq(q.trim());
          }}
        >
          <input className="input" type="search" value={q} onChange={(e) => (setQ(e.target.value), e.target.value.trim().length < 2 && setSq(''))} placeholder={t('ot.struct_search', { leaf: leafText(t, props.settings, props.org.kind === 'personal') })} aria-label={t('ot.struct_search', { leaf: leafText(t, props.settings, props.org.kind === 'personal') })} />
        </form>
      )}
      {sq.length >= 2 ? (
        <ul className="plain-list ot-results" data-testid="struct-results">
          {search.isLoading && <li className="muted small">{t('common.loading')}</li>}
          {(search.data?.items ?? []).length === 0 && !search.isLoading && <li className="muted small">{t('ot.no_results')}</li>}
          {(search.data?.items ?? []).map((it) => (
            <li key={it.node_id ?? it.home_id}>
              <button
                className="link-btn"
                onClick={() => {
                  if (it.kind === 'node' && it.node_id) props.onSelect?.({ node_id: it.node_id, name: it.name, level_label: it.level_label ?? null, isRoot: false });
                  else if (props.onPickHome) props.onPickHome(it);
                  else if (it.parent_node_id) props.onSelect?.({ node_id: it.parent_node_id, name: '', level_label: null, isRoot: it.parent_node_id === rootId });
                  setSq('');
                  setQ('');
                }}
              >
                {it.name}
              </button>{' '}
              <span className="ot-chip">{it.kind === 'home' ? leafText(t, props.settings, props.org.kind === 'personal') : levelText(t, it.level_label)}</span>
              {it.path && <span className="muted small"> {it.path.map((p) => p.name).join(' › ')}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <ul className="ot-tree" role="tree" aria-label={t('ot.struct_tree')}>
          {rootId && root.data?.root ? (
            <li role="treeitem" aria-expanded aria-selected={props.selected === rootId}>
              <div className={'ot-row' + (props.selected === rootId ? ' sel' : '')}>
                <span className="ot-twisty">▾</span>
                <button className="ot-name" onClick={() => props.onSelect?.({ node_id: rootId, name: rootName, level_label: null, isRoot: true })}>
                  ▦ {rootName}
                </button>
                <span className="spacer" />
                {!props.pick && root.data.root.subtree && (
                  <span className="ot-count" title={countTip(t, root.data.root.subtree)}>
                    {root.data.root.subtree.devices}
                  </span>
                )}
              </div>
              <ul role="group">
                <Items {...props} items={root.data?.items ?? []} depth={1} open={open} setOpen={setOpen} />
              </ul>
            </li>
          ) : (
            <Items {...props} items={root.data?.items ?? []} depth={0} open={open} setOpen={setOpen} />
          )}
        </ul>
      )}
    </div>
  );
}

function Items(
  props: Parameters<typeof StructureTree>[0] & { items: OrgTreeItem[]; depth: number; open: Set<string>; setOpen(f: (s: Set<string>) => Set<string>): void },
) {
  const { t } = useTranslation();
  const { items, depth, open, setOpen, pick } = props;
  const nodes = items.filter((x) => x.kind === 'node' && x.node_id !== pick?.exclude && x.status !== 'archived');
  const homes = pick || props.nodeChecks ? [] : items.filter((x) => x.kind === 'home');
  return (
    <>
      {nodes.map((it) => {
        const id = it.node_id!;
        const isOpen = open.has(id);
        const sel = props.selected === id;
        return (
          <li key={id} role="treeitem" aria-expanded={it.has_children !== false ? isOpen : undefined} aria-selected={sel}>
            <div className={'ot-row' + (sel ? ' sel' : '')} style={{ paddingLeft: depth * 16 }} data-node={it.name}>
              {it.has_children !== false ? (
                <button className="ot-twisty" aria-label={isOpen ? t('ot.collapse') : t('ot.expand')} onClick={() => setOpen((s) => toggle(s, id))}>
                  {isOpen ? '▾' : '▸'}
                </button>
              ) : (
                <span className="ot-twisty" />
              )}
              {props.nodeChecks ? (
                <label className="row-inline">
                  <input
                    type="checkbox"
                    disabled={props.nodeChecks.disabled}
                    checked={props.nodeChecks.disabled || props.nodeChecks.set.has(id)}
                    onChange={() => props.nodeChecks!.toggle(it)}
                    aria-label={it.name}
                  />
                  {it.name}
                </label>
              ) : (
                <button className="ot-name" onClick={() => props.onSelect?.({ node_id: id, name: it.name, level_label: it.level_label ?? null, isRoot: false })}>
                  {it.name}
                </button>
              )}
              {it.level_label && <span className="ot-chip">{levelText(t, it.level_label)}</span>}
              {props.showLock && it.block_inheritance && (
                <span className="ot-lock" title={t('ot.blocked_tip')} aria-label={t('ot.blocked_tip')}>
                  🔒
                </span>
              )}
              <span className="spacer" />
              {props.onAdd && (
                <button className="btn btn-sm ot-add" onClick={() => props.onAdd!(it)} aria-label={t('ot.add_ref', { name: it.name })}>
                  ＋
                </button>
              )}
              {!pick && it.subtree && (
                <span className="ot-count" title={countTip(t, it.subtree)}>
                  {it.subtree.devices}
                </span>
              )}
            </div>
            {isOpen && (
              <ul role="group">
                <Children {...props} parent={id} depth={depth + 1} />
              </ul>
            )}
          </li>
        );
      })}
      {homes.map((h) => {
        const on = props.checked?.has(h.home_id!) ?? false;
        const hsel = !!props.selectedHome && props.selectedHome === h.home_id;
        return (
          <li key={h.home_id} role="treeitem" aria-selected={props.onPickHome ? hsel : undefined}>
            <div className={'ot-row ot-home' + (hsel ? ' sel' : '')} style={{ paddingLeft: depth * 16 }} data-home={h.name}>
              <span className="ot-twisty" />
              {props.onPickHome ? (
                <button className="ot-name" onClick={() => props.onPickHome!(h)}>
                  {h.name}
                </button>
              ) : props.onCheck ? (
                <label className="row-inline">
                  <input type="checkbox" checked={on} onChange={(e) => props.onCheck?.(h, e.target.checked)} aria-label={t('ot.select_home', { name: h.name })} />
                  {h.name}
                </label>
              ) : (
                <span>{h.name}</span>
              )}
              <span className="ot-chip ot-leaf">{leafText(t, props.settings, props.org.kind === 'personal')}</span>
              {props.showUnassigned && h.management_status === 'unassigned' && <span className="badge badge-warn">{t('ot.unassigned')}</span>}
              <span className="spacer" />
              {props.onAdd && (
                <button className="btn btn-sm ot-add" onClick={() => props.onAdd!(h)} aria-label={t('ot.add_ref', { name: h.name })}>
                  ＋
                </button>
              )}
              <span className="ot-count" title={countTip(t, h.subtree ?? { devices: h.devices ?? 0 })}>
                {h.devices ?? h.subtree?.devices ?? 0}
              </span>
            </div>
          </li>
        );
      })}
    </>
  );
}

function Children(props: Parameters<typeof StructureTree>[0] & { parent: string; depth: number; open: Set<string>; setOpen(f: (s: Set<string>) => Set<string>): void }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['ot', props.org.org_id, 'nodes', props.parent], queryFn: () => treeApi.children(props.org, props.parent) });
  if (q.isLoading) return <li className="muted small" style={{ paddingLeft: props.depth * 16 + 20 }}>{t('common.loading')}</li>;
  if (q.isError) return <li className="error small">{t('common.error')}</li>;
  return <Items {...props} items={q.data?.items ?? []} />;
}

/** 數字的滑過說明：台數不含通道；舊後端（沒分開回通道）寫「含 NVR 通道」（SPEC #org-tree-ui，第二輪 R2-P1-11）。 */
function countTip(t: (k: string, o?: Record<string, unknown>) => string, s: { devices: number; channels?: number }): string {
  const c = structCount(s);
  if (!c || c.inclChannels) return t('ot.struct_count_tip_incl');
  return t('ot.struct_count_tip_split', { channels: c.channels ?? 0 });
}
